class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;

  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    this.triggerQuery = [
      ".wt-cart__trigger",
      ".wt-cart__back-link",
      `.${this.pageOverlayClass}`,
    ].join(", ");

    this.triggers = () =>
      document.querySelectorAll(this.triggerQuery);

    this.isOpen = false;
    this.isCartPage =
      window.location.pathname === window.routes.cart_url;

    this.closeButton = () =>
      this.querySelector(".wt-cart__drawer__close");

    this.mainTrigger =
      document.querySelector(".wt-cart__trigger");

    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    this.openerEl = null;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", () =>
        this.refreshCartDrawer()
      );
      return;
    }

    this.init();

    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      () => {
        if (this.isOpen) {
          setTabindex(this.toggleEelements(), "0");
          this.closeButton()?.focus();
        }
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  rememberOpener(e) {
    const current = e?.currentTarget;
    const active = document.activeElement;

    this.openerEl =
      current instanceof HTMLElement
        ? current
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;

    if (!el || !document.contains(el)) return;

    if (
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true"
    ) {
      return;
    }

    requestAnimationFrame(() => el.focus?.());
  }

  getFocusableElements() {
    const selector =
      "button, [href], input, select, [tabindex]";

    const focusableElements = () =>
      Array.from(this.querySelectorAll(selector)).filter(
        (el) =>
          !el.hasAttribute("disabled") && el.tabIndex >= 0
      );

    const elements = focusableElements();

    return {
      focusableElements,
      first: elements[0],
      last: elements[elements.length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;

      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
      this.restoreFocusToOpener();
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;

      this.closeButton()?.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.drawer = this.querySelector(`.${this.drawerClass}`);

    this.onToggle();

    this.drawer?.classList.toggle(
      this.classDrawerActive,
      this.isOpen
    );

    this.body.classList.toggle(
      this.activeOverlayBodyClass,
      this.isOpen
    );

    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      })
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        e.preventDefault();
        this.toggleDrawerClasses();
        return;
      }

      if (e.key !== "Tab" || !this.isOpen) return;

      const { first, last } = this.getFocusableElements();

      if (!first || !last) return;

      if (
        e.shiftKey &&
        document.activeElement === first
      ) {
        last.focus();
        e.preventDefault();
      } else if (
        !e.shiftKey &&
        document.activeElement === last
      ) {
        first.focus();
        e.preventDefault();
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        const target =
          e.target instanceof Element ? e.target : null;

        // Let the close-button handler handle the close icon.
        if (target?.closest(".wt-cart__drawer__close")) {
          return;
        }

        e.preventDefault();

        // The overlay and back link should only close the drawer.
        if (
          trigger.matches(
            `.wt-cart__back-link, .${this.pageOverlayClass}`
          )
        ) {
          if (this.isOpen) {
            this.toggleDrawerClasses();
          }
          return;
        }

        if (!this.isOpen) {
          this.rememberOpener(e);
        }

        this.toggleDrawerClasses();
      });
    });

    // Handle clicks on the close button or its nested SVG/icon.
    this.addEventListener("click", (e) => {
      const target =
        e.target instanceof Element ? e.target : null;

      const closeButton = target?.closest(
        ".wt-cart__drawer__close"
      );

      if (!closeButton || !this.contains(closeButton)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (this.isOpen) {
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", () => {
      this.refreshCartDrawer();
    });
  }

  renderContents(parsedState, isClosedCart = true) {
    if (!parsedState?.sections) return;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML = parsedState.sections[section.id];

      if (!sectionElement || !sectionHTML) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );
    });

    // Preserve the original AJAX add-to-cart drawer-opening behavior.
    if (isClosedCart) {
      setTimeout(() => {
        this.drawer = this.querySelector(`.${this.drawerClass}`);

        if (!this.isOpen) {
          this.toggleDrawerClasses();
        }

        if (this.isOpen) {
          this.closeButton()?.focus();
        }
      });
    }
  }

  getSectionInnerHTML(
    html,
    selector = ".shopify-section"
  ) {
    const documentHTML = new DOMParser().parseFromString(
      html,
      "text/html"
    );

    const section = documentHTML.querySelector(selector);

    return section ? section.innerHTML : html;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer() {
    const sectionsToRender = this.getSectionsToRender();

    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender
        .map((section) => section.id)
        .join(",")}`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to refresh cart drawer.");
        }

        return response.json();
      })
      .then((response) => {
        this.renderContents(
          { sections: response },
          false
        );
      })
      .catch((error) => {
        console.error("Cart drawer refresh failed:", error);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawerSection);
}

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to update cart items.");
        }

        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(
          responseText,
          "text/html"
        );

        const source = html.querySelector("cart-drawer-items");

        if (source) {
          this.innerHTML = source.innerHTML;
        }
      })
      .catch((error) => {
        console.error("Cart items update failed:", error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

if (!customElements.get("cart-drawer-items")) {
  customElements.define(
    "cart-drawer-items",
    CartDrawerItems
  );
}
